import { Button } from "@miror-ui/components";
import { Avatar } from "shared/components/atoms/Avatar";
import { Image } from "shared/components/atoms/Image";
import {
  BaseModal,
  BaseModalProps,
} from "shared/components/organisms/modal/BaseModal";
import { CalledFortuneTeller, TelWaitingFortuneTeller } from "shared/types";
import tw, { css } from "twin.macro";
import FilledPhoneIcon from "shared/assets/icons/FilledPhone.svg";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { selectAvailableCoupons } from "features/shared/utils/coupon";
import { useMe, useUserCoupons } from "repositories/hooks";
import OrderPaymentRepository from "repositories/OrderInfoRepository";
import { useErrorMessage } from "shared/hooks/useErrorMessage";
import CallRepository from "repositories/call";
import { useRouter } from "next/router";
import { useCallInfo } from "../hooks/useCallInfo";

const radialGradientCss = css({
  background: "radial-gradient(circle at top, #FF4CE1, #FF9892)",
});

interface Props extends BaseModalProps {
  fortuneTeller: CalledFortuneTeller | TelWaitingFortuneTeller;
}

export const OrderModal = ({ fortuneTeller }: Props) => {
  const [processing, setProcessing] = useState(false);

  const { me } = useMe();
  const { validCoupons } = useUserCoupons();
  const { push } = useRouter();
  const { handleAxiosError } = useErrorMessage();
  const callInfo = useCallInfo();

  const availableCoupons = selectAvailableCoupons(validCoupons, fortuneTeller);
  const coupon = availableCoupons
    .filter((c) => c.forType === "tel")
    .sort((a, b) => b.discountAmount - a.discountAmount)[0];

  const waitedAt = useMemo(() => {
    if ("lastTelWaitingStartedAt" in fortuneTeller) {
      return dayjs(fortuneTeller.lastTelWaitingStartedAt);
    } else if ("currentTelWaitingHistory" in fortuneTeller) {
      return dayjs(fortuneTeller.currentTelWaitingHistory.startedAt);
    }
  }, [fortuneTeller]);

  const handleClickButton = useCallback(async () => {
    if (processing) return;

    setProcessing(true);

    try {
      const result = await OrderPaymentRepository.post({
        _type: "tel",
        menuId: fortuneTeller.popularTelFortune.id,
        phoneNumber: me.phoneNumber,
        paymentMethod: "credit_card",
        usePoint: false,
        useCoupon: !!coupon,
        useCouponId: String(coupon?.id),
        useUserCouponOwnershipId: String(coupon?.ownershipId || coupon?.id),
        name: me.name,
        birthday: me.birthday.toISOString(),
        gender: me.gender,
        bloodtype: me.bloodtype,
        fromNow: true,
        consultContent: null,
        cvsName: null,
        firstName: null,
        lastName: null,
        lastNameKana: null,
      });

      if (result.id) {
        await CallRepository.get(result.id);
        await push(`/tel_fortune_chat_rooms/${result.chatRoomId}`);
      }
    } catch (error) {
      handleAxiosError(error);
    } finally {
      setProcessing(false);
    }
  }, [fortuneTeller, coupon, me, push, handleAxiosError, processing]);

  return (
    <BaseModal title="鑑定通話を開始しますか？">
      <div tw="flex-center flex-col">
        <Avatar src={fortuneTeller.imageUrl} size={79} tw="mt-[22px]" />
        <h5 tw="font-base text-xs tracking-[.35px] mt-2">
          {fortuneTeller.name}先生
        </h5>
        <p tw="font-semibold text-[9px] tracking-[.42px] text-[#FF9892] mt-2">
          {waitedAt.format("HH:mm")}〜待機中
        </p>
        <div tw="border border-solid border-gray rounded font-base text-[9px] tracking-[.26px] text-left mt-4">
          <div
            tw="grid grid-flow-col gap-x-2 items-center border-b border-solid border-gray p-3"
            css={{ gridAutoColumns: "auto 1fr" }}
          >
            <Image
              src={fortuneTeller.popularTelFortune.imageUrl}
              width={32}
              height={32}
              tw="rounded"
            />
            <h6 tw="leading-4 line-clamp-3">
              {fortuneTeller.popularTelFortune.title}
            </h6>
          </div>
          <div tw="grid gap-y-[9px] p-3">
            <p>
              鑑定料 : {fortuneTeller.popularTelFortune.costPerMinute}円/分 +
              通話料 : 30円/分
            </p>
            <p>ご利用クーポン : {coupon?.name}</p>
          </div>
        </div>
        <Button
          tw="rounded-md h-12 mt-4"
          css={
            processing
              ? tw`cursor-not-allowed from-secondary to-secondary`
              : radialGradientCss
          }
          onClick={handleClickButton}
          disabled={processing}
        >
          <FilledPhoneIcon tw="mr-[6.77px]" css={{ transform: "scale(1.2)" }} />
          通話を開始する
        </Button>
        <button
          onClick={() =>
            callInfo.goToSelectTelMenu({ fortuneTeller, fromNow: true })
          }
          tw="font-semibold text-xs tracking-[.5px] leading-none text-[#FF9892] mt-4"
        >
          申し込み内容を変更する
        </button>
      </div>
    </BaseModal>
  );
};
